<template>
  <div class="max-w-lg px-4 py-12 mx-auto sm:px-6 lg:px-8">
    <!-- Billing frequency toggle -->
    <fieldset
      aria-label="Billing frequency"
      class="flex justify-center mb-8 space-x-2"
    >
      <RadioGroup
        v-model="selectedPlanId"
        class="grid grid-cols-2 p-1 text-sm font-semibold text-center rounded-full gap-x-1 ring-1 ring-inset ring-gray-200"
      >
        <RadioGroupOption
          as="template"
          v-for="plan in plans"
          :key="plan.id"
          :value="plan.id"
          v-slot="{ checked }"
        >
          <button
            :class="[
              checked
                ? 'bg-electric-700 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50',
              'cursor-pointer rounded-full px-4 py-1 transition'
            ]"
          >
            {{ plan.nameLabel }}
          </button>
        </RadioGroupOption>
      </RadioGroup>
    </fieldset>

    <!-- Single plan card -->
    <div
      :class="[
        'p-8 ring-1 rounded-3xl',
        selectedPlan.id === selectedPlanId
          ? 'ring-electric-600 bg-electric-50/50'
          : 'ring-gray-200'
      ]"
    >
      <div
        v-if="selectedPlan.id === 'yearly'"
        class="float-right px-3 py-1 mb-2 -mt-5 text-sm font-semibold rounded-lg text-electric-800 bg-electric-100"
      >
        Save 40%
      </div>

      <h2
        :class="[
          'text-lg font-bold leading-8 uppercase',
          selectedPlan.id === selectedPlanId
            ? 'text-electric-700'
            : 'text-gray-900'
        ]"
      >
        PRO {{ selectedPlan.nameLabel }}
      </h2>

      <p class="flex items-baseline mt-6 gap-x-1">
        <span class="text-4xl font-bold tracking-tight text-gray-900">
          {{ selectedPlan.priceLabel }}
        </span>
        <span class="text-sm font-semibold leading-6 text-gray-600">
          /month
        </span>
      </p>

      <p class="mt-3 text-sm leading-6 text-gray-600">
        {{ selectedPlan.radioDesc }}
      </p>

      <div class="mt-6">
        <a
          :href="`/membership/checkout/${selectedPlan.id}`"
          :class="[
            'w-full block px-4 py-2 text-sm font-semibold leading-6 text-center rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2',
            selectedPlan.id === selectedPlanId
              ? 'bg-electric-700 text-white hover:bg-electric-800'
              : 'text-electric-700 ring-1 ring-inset ring-electric-200 hover:ring-electric-300'
          ]"
        >
          Choose Plan
        </a>
      </div>
      <div class="mt-8">
        
        <ul class="mt-3 space-y-3">
          <li class="flex items-start gap-4 text-sm/6 text-gray-950/75 data-disabled:text-gray-950/25">
            <span class="inline-flex items-center h-6">
              <svg class="flex-none w-5 h-6 text-electric-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd"></path>
              </svg>
            </span>
            Full access to expert-written show guides
          </li>
          <li class="flex items-start gap-4 text-sm/6 text-gray-950/75 data-disabled:text-gray-950/25">
            <span class="inline-flex items-center h-6">
              
              <svg class="flex-none w-5 h-6 text-electric-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd"></path>
              </svg>
            </span>
            Detailed monologue and scene context
          </li>
          <li class="flex items-start gap-4 text-sm/6 text-gray-950/75 data-disabled:text-gray-950/25">
            <span class="inline-flex items-center h-6">
              <svg class="flex-none w-5 h-6 text-electric-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd"></path>
              </svg>
            </span>
            25,000+ character breakdowns
          </li>
          <li class="flex items-start gap-4 text-sm/6 text-gray-950/75 data-disabled:text-gray-950/25">
            <span class="inline-flex items-center h-6">
              <svg class="flex-none w-5 h-6 text-electric-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd"></path>
              </svg>
            </span>
            Song-specific vocal notes
          </li>
          <li class="flex items-start gap-4 text-sm/6 text-gray-950/75 data-disabled:text-gray-950/25">
            <span class="inline-flex items-center h-6">
              <svg class="flex-none w-5 h-6 text-electric-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd"></path>
              </svg>
            </span>
            Skill-building learning modules
          </li>
          <li class="flex items-start gap-4 text-sm/6 text-gray-950/75 data-disabled:text-gray-950/25">
            <span class="inline-flex items-center h-6">
              <svg class="flex-none w-5 h-6 text-electric-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd"></path>
              </svg>
            </span>
            Curated audition piece suggestions
          </li>
          <li class="flex items-start gap-4 text-sm/6 text-gray-950/75 data-disabled:text-gray-950/25">
            <span class="inline-flex items-center h-6">
              <svg class="flex-none w-5 h-6 text-electric-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd"></path>
              </svg>
            </span>
            Advanced search filters
          </li>
          <li class="flex items-start gap-4 text-sm/6 text-gray-950/75 data-disabled:text-gray-950/25">
            <span class="inline-flex items-center h-6">
              <svg class="flex-none w-5 h-6 text-electric-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd"></path>
              </svg>
            </span>
            Downloadable theatre templates
          </li>
          <li class="flex items-start gap-4 text-sm/6 text-gray-950/75 data-disabled:text-gray-950/25">
            <span class="inline-flex items-center h-6">
              <svg class="flex-none w-5 h-6 text-electric-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd"></path>
              </svg>
            </span>
            Post and promote auditions or jobs
          </li>
          <li class="flex items-start gap-4 text-sm/6 text-gray-950/75 data-disabled:text-gray-950/25">
            <span class="inline-flex items-center h-6">
              <svg class="flex-none w-5 h-6 text-electric-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd"></path>
              </svg>
            </span>
            Direct messaging with talent
          </li>
          <li class="flex items-start gap-4 text-sm/6 text-gray-950/75 data-disabled:text-gray-950/25">
            <span class="inline-flex items-center h-6">
              <svg class="flex-none w-5 h-6 text-electric-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd"></path>
              </svg>
            </span>
            Highlight your profile for increased visibility
          </li>
          <li class="flex items-start gap-4 text-sm/6 text-gray-950/75 data-disabled:text-gray-950/25">
            <span class="inline-flex items-center h-6">
              <svg class="flex-none w-5 h-6 text-electric-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd"></path>
              </svg>
            </span>
            Members-only blog content
          </li>
          <li class="flex items-start gap-4 text-sm/6 text-gray-950/75 data-disabled:text-gray-950/25">
            <span class="inline-flex items-center h-6">
              <svg class="flex-none w-5 h-6 text-electric-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd"></path>
              </svg>
            </span>
            Enjoy StageAgent ad-free
          </li>
        </ul>
      </div>
      
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { RadioGroup, RadioGroupOption } from "@headlessui/vue";

const plans = [
  {
    id: "monthly",
    nameLabel: "Monthly",
    priceLabel: "$19.95 USD",
    radioDesc: "Billed $19.95 USD every month"
  },
  {
    id: "yearly",
    nameLabel: "Yearly",
    priceLabel: "$11.95 USD",
    radioDesc: "Billed $143.40 USD every year"
  }
];

const selectedPlanId = ref<"monthly" | "yearly">("yearly");

const selectedPlan = computed(
  () => plans.find(p => p.id === selectedPlanId.value) || plans[0]
);
</script>